import { saveAs } from 'file-saver';
import { IDocument } from 'types';
import JSZip from 'jszip';
import { useState, useCallback } from 'react';
import { Api } from '@koopajs/app';
import { useAppSnackbar } from 'components/hooks/useAppSnackbar';
import { useLocale } from '@koopajs/react';

const getUniqueFileName = (fileName: string, fileNameCounts: Record<string, number>): string => {
  if (!fileNameCounts[fileName]) {
    fileNameCounts[fileName] = 1;
    return fileName;
  }

  const fileExtension = fileName.lastIndexOf('.') > -1 ? fileName.substring(fileName.lastIndexOf('.')) : '';
  const fileNameWithoutExtension =
    fileName.lastIndexOf('.') > -1 ? fileName.substring(0, fileName.lastIndexOf('.')) : fileName;

  fileNameCounts[fileName]++;

  // Create the new file name with the counter in parentheses
  return `${fileNameWithoutExtension} (${fileNameCounts[fileName] - 1})${fileExtension}`;
};

interface IDownloadDocumentsAsZipProps {
  folderName: string;
}

interface IDownloadDocumentsParams {
  documents: IDocument[];
}

export const useDownloadDocumentsAsZip = (
  props: IDownloadDocumentsAsZipProps
): {
  downloadDocumentsAsZip: (params?: IDownloadDocumentsParams) => Promise<void>;
  isDownloading: boolean;
} => {
  const { t } = useLocale();
  const keyPrefix = 'hooks.useDownloadDocumentsAsZip';

  const { triggerSnackbar } = useAppSnackbar();

  const [isDownloading, setIsDownloading] = useState(false);
  const { folderName } = props;

  const downloadDocumentsAsZip = useCallback(
    async (params?: IDownloadDocumentsParams) => {
      const documents = params?.documents;
      if (!documents || documents.length === 0) {
        return;
      }

      setIsDownloading(true);

      try {
        const zip = new JSZip();
        const folder = zip.folder(folderName);
        const fileNameCounts: Record<string, number> = {}; // Track file name counts

        if (!folder) {
          throw new Error('Failed to create zip folder');
        }

        // Create an array of promises for downloading each document concurrently
        const downloadPromises = documents.map(async (doc) => {
          try {
            const url = `/documents/${doc.id}`;
            const documentResponse = await Api.client.get(url);
            const downloadUrl = documentResponse.data.resource.attachedFileUriDownload;

            const response = await fetch(downloadUrl);
            if (!response.ok) {
              throw new Error(`Failed to download file: Status ${response.status}`);
            }

            const blob = await response.blob();

            const originalFileName = doc.attachedFile.name || `document_${doc.id}.pdf`;
            const uniqueFileName = getUniqueFileName(originalFileName, fileNameCounts);

            folder.file(uniqueFileName, blob);
          } catch (error) {
            triggerSnackbar({
              snackbarText: t(keyPrefix + '.snackbarDownloadFail', {
                fileName: doc.attachedFile.name || `document_${doc.id}.pdf`
              }),
              variant: 'error'
            });
          }
        });

        await Promise.all(downloadPromises);

        const zipBlob = await zip.generateAsync({ type: 'blob' });

        // Trigger the download
        saveAs(zipBlob, `${folderName}.zip`);
      } catch (error) {
        triggerSnackbar({
          snackbarText: t(keyPrefix + '.snackbarDownloaDocumentsFail'),
          variant: 'error'
        });
      } finally {
        setIsDownloading(false);
      }
    },
    [folderName]
  );

  return { downloadDocumentsAsZip, isDownloading };
};
